import React from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Search, Bell } from "lucide-react";
import { useI18n } from "@/context/PrefsContext";

export function SearchBar({ value, onChange, placeholder, testid }) {
  const { t } = useI18n();
  return (
    <div className="relative w-full sm:w-80">
      <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9b8bb5]" />
      <input
        value={value}
        onChange={onChange}
        placeholder={placeholder || t("Search jobs, customers, crews...")}
        data-testid={testid}
        className="w-full bg-[#F1EBFA] border border-transparent rounded-full pl-11 pr-4 py-2.5 text-sm text-[#121212] placeholder:text-[#9b8bb5] outline-none focus:bg-white focus:border-[#5A189A]/30 focus:ring-2 focus:ring-[#5A189A]/15 transition"
      />
    </div>
  );
}

export function PageHeader({ title, subtitle, action, search, searchValue, onSearch, searchPlaceholder }) {
  const { t } = useI18n();
  return (
    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-7">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#1a1523] tracking-tight">{t(title)}</h1>
        {subtitle && <p className="text-[#6b647a] mt-1 text-sm">{t(subtitle)}</p>}
      </div>
      <div className="flex items-center gap-3">
        {search && <SearchBar value={searchValue} onChange={onSearch} placeholder={searchPlaceholder} testid="page-search" />}
        <button className="hidden sm:flex relative w-11 h-11 rounded-full bg-[#F1EBFA] items-center justify-center text-[#5A189A] hover:bg-[#e7ddf5] transition" data-testid="notifications-bell">
          <Bell size={19} />
          <span className="absolute top-2.5 right-2.5 w-2 h-2 rounded-full bg-[#D4AF37]" />
        </button>
        {action}
      </div>
    </div>
  );
}

/* Gold primary button */
export function PrimaryButton({ children, className = "", ...props }) {
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 font-semibold rounded-xl px-5 py-2.5 text-sm text-[#3a2a00] shadow-[0_6px_16px_-4px_rgba(212,175,55,0.5)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_22px_-4px_rgba(212,175,55,0.6)] ${className}`}
      style={{ background: "linear-gradient(135deg, #F0C94A 0%, #D4AF37 100%)" }}
      {...props}
    >
      {children}
    </button>
  );
}

/* Purple secondary button */
export function SecondaryButton({ children, className = "", ...props }) {
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 font-semibold rounded-xl px-5 py-2.5 text-sm text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:opacity-95 ${className}`}
      style={{ background: "linear-gradient(135deg, #7B2CBF 0%, #5A189A 100%)" }}
      {...props}
    >
      {children}
    </button>
  );
}

export function StatCard({ icon: Icon, label, value, accent = "#5A189A", testid }) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-[#f0ebf7] shadow-[0_4px_18px_-8px_rgba(90,24,154,0.15)] flex flex-col gap-3 hover:shadow-[0_8px_26px_-8px_rgba(90,24,154,0.25)] transition-all" data-testid={testid}>
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: `${accent}1a`, color: accent }}
      >
        <Icon size={20} strokeWidth={1.8} />
      </div>
      <div>
        <div className="text-2xl font-bold text-[#1a1523] font-display">{value}</div>
        <div className="text-sm text-[#6b647a] mt-0.5">{label}</div>
      </div>
    </div>
  );
}

export function Badge({ children, color = "#5A189A" }) {
  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
      style={{ backgroundColor: `${color}1a`, color }}
    >
      {children}
    </span>
  );
}

export function ServiceTag({ name, color = "#7B2CBF" }) {
  if (!name) return null;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
      style={{ backgroundColor: `${color}14`, color }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      {name}
    </span>
  );
}

export const STATUS_COLORS = {
  New: "#7B2CBF", Contacted: "#D4AF37", Qualified: "#0ea5e9", Converted: "#16a34a",
  Draft: "#52525B", Sent: "#0ea5e9", Approved: "#16a34a", Declined: "#ef4444",
  Scheduled: "#7B2CBF", "In Progress": "#D4AF37", Completed: "#16a34a", Cancelled: "#ef4444",
  Unpaid: "#ef4444", Partial: "#D4AF37", Paid: "#16a34a",
  Active: "#16a34a", Inactive: "#52525B", Pending: "#D4AF37",
};

export function StatusBadge({ status }) {
  const { t } = useI18n();
  return <Badge color={STATUS_COLORS[status] || "#5A189A"}>{t(status)}</Badge>;
}

export function Field({ label, children, className = "" }) {
  const { t } = useI18n();
  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      {label && <label className="text-sm font-medium text-[#6b647a]">{t(label)}</label>}
      {children}
    </div>
  );
}

export function Input(props) {
  return (
    <input
      {...props}
      className={`w-full border border-[#e7ddf5] rounded-xl px-3.5 py-2.5 text-sm text-[#121212] bg-white outline-none focus:ring-2 focus:ring-[#5A189A]/20 focus:border-[#5A189A] transition ${props.className || ""}`}
    />
  );
}

export function Textarea(props) {
  return (
    <textarea
      {...props}
      className={`w-full border border-[#e7ddf5] rounded-xl px-3.5 py-2.5 text-sm text-[#121212] bg-white outline-none focus:ring-2 focus:ring-[#5A189A]/20 focus:border-[#5A189A] transition ${props.className || ""}`}
    />
  );
}

export function SelectBox({ value, onChange, options, placeholder, ...rest }) {
  const { t } = useI18n();
  return (
    <select
      value={value}
      onChange={onChange}
      {...rest}
      className="w-full border border-[#e7ddf5] rounded-xl px-3.5 py-2.5 text-sm text-[#121212] bg-white outline-none focus:ring-2 focus:ring-[#5A189A]/20 focus:border-[#5A189A] transition"
    >
      {placeholder && <option value="">{t(placeholder)}</option>}
      {options.map((o) => {
        const val = typeof o === "string" ? o : o.value;
        const lbl = typeof o === "string" ? o : o.label;
        return (
          <option key={val} value={val}>
            {t(lbl)}
          </option>
        );
      })}
    </select>
  );
}

export function Modal({ open, onClose, title, children, footer, size = "lg" }) {
  const { t } = useI18n();
  const widths = { sm: "max-w-md", md: "max-w-xl", lg: "max-w-2xl", xl: "max-w-4xl" };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`${widths[size]} max-h-[90vh] overflow-y-auto rounded-2xl`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1a1523]">{t(title)}</DialogTitle>
        </DialogHeader>
        <div className="py-2">{children}</div>
        {footer && <DialogFooter>{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

export function EmptyState({ icon: Icon, title, subtitle, action }) {
  const { t } = useI18n();
  return (
    <div className="bg-white rounded-2xl border border-[#f0ebf7] shadow-sm p-12 flex flex-col items-center text-center" data-testid="empty-state">
      <div className="w-16 h-16 rounded-2xl bg-[#5A189A]/10 text-[#5A189A] flex items-center justify-center mb-4">
        <Icon size={28} strokeWidth={1.6} />
      </div>
      <h3 className="font-display text-xl font-semibold text-[#1a1523]">{t(title)}</h3>
      {subtitle && <p className="text-[#6b647a] text-sm mt-1 max-w-sm">{t(subtitle)}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function TableShell({ headers, children, testid }) {
  const { t } = useI18n();
  return (
    <div className="bg-white rounded-2xl border border-[#f0ebf7] shadow-[0_4px_18px_-10px_rgba(90,24,154,0.18)] overflow-hidden" data-testid={testid}>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-[#faf8fd] border-b border-[#efeaf6]">
              {headers.map((h) => (
                <th key={h} className="py-3.5 px-4 text-xs font-semibold uppercase tracking-wider text-[#6b647a]">
                  {t(h)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}

export { Button };
